/**
 * SalesOrder(出貨單 / 應收單)zod schema —— 前後端共用。
 *
 * 兩條寫入路徑:
 *  1. 從訪談一鍵建立應收單(REQ-20260730)—— 單品項、直接過帳
 *  2. 會計「出貨管理」的完整建單(REQ-20260806002)—— 多品項、才數 / 模數計價、
 *     版費 / 刀模費、草稿 → 過帳 → 作廢
 */
import { z } from 'zod';
import type { SalesOrderTaxMode } from '../utils/accounting';
import { decimalString, nonNegativeDecimalString, positiveDecimalString } from './decimal.schema';
import type { OrderShipmentStatus } from './shipment.schema';

/** POST /interviews/:id/receivable body。 */
export const CreateInterviewReceivableSchema = z
  .object({
    productName: z.string().trim().min(1, '品名必填').max(60),
    /** 貨品類別:設定頁「貨品類別」的 id(REQ-20260807002) */
    productCategoryId: z.number().int().positive().optional().nullable(),
    productSpec: z.string().trim().max(60).optional().nullable(),
    productMaterial: z.string().trim().max(60).optional().nullable(),
    /**
     * 加工:REQ-20260810001 起可複選常用選項,多個用「、」串接成一個字串送過來,
     * 所以上限跟著 DB 從 60 加寬到 255(對齊 sales_order_lines.productProcessing)。
     */
    productProcessing: z.string().trim().max(255).optional().nullable(),
    /**
     * 藝品印刷訂單的三個現場欄位(REQ-20260809002)。單頭欄位,不是逐品項 ——
     * 一張單只有一種印刷顏色 / 出貨方式。
     * REQ-20260810001:印刷顏色 / 稿件處理同樣改為可複選串接 → max 255;
     * 出貨方式維持單選(需求明確要求不可複選,但可手動輸入)→ 仍是 60。
     */
    printColor: z.string().trim().max(255).optional().nullable(),
    artworkHandling: z.string().trim().max(255).optional().nullable(),
    shippingMethod: z.string().trim().max(60).optional().nullable(),
    /**
     * 印刷面別:單面 / 雙面(REQ-20260810003)。同為單頭欄位 ——
     * 一張單只會有一種面別。**單選**,所以與 shippingMethod 一樣是 60 而不是 255。
     */
    printSides: z.string().trim().max(60).optional().nullable(),
    /**
     * 帳款對象(REQ-20260810002)。沒帶時後端寫入 DB 預設值 `'COMPANY'`,
     * 舊 client 的行為完全不變。選 `'PERSONAL'` 時後端會順手把來源訪談標成私帳。
     */
    payerType: z.enum(['COMPANY', 'PERSONAL']).optional(),
    /** 帳款抬頭 / 實際收款對象姓名。公司公帳留空即沿用客戶簡稱 */
    payerName: z.string().trim().max(100).optional().nullable(),
    /**
     * 單頭的收執憑證 / 收款方式覆寫。null / 沒帶 = 沿用客戶主檔設定(既有行為)。
     * id 來自設定頁既有的「收款憑證」「收款方式」兩張表,不是新的值域。
     */
    paymentCertId: z.number().int().positive().optional().nullable(),
    paymentMethodId: z.number().int().positive().optional().nullable(),
    qty: decimalString,
    /**
     * 單價與總價擇一(REQ-20260807002):有些報價談的是整批總價,
     * 逼使用者自己除以數量得到的單價會有除不盡的尾差。兩者都由後端換算,
     * 前端不決定金額。
     */
    unitPrice: decimalString.optional(),
    totalPrice: decimalString.optional(),
    unitName: z.string().trim().max(30).optional().nullable(),
    taxRate: decimalString.optional(),
    /** 稅別(REQ-20260806003)。內含 = 輸入金額已含稅,由後端反推未稅與稅額 */
    taxMode: z.enum(['EXCLUSIVE', 'INCLUSIVE', 'EXEMPT']).default('EXCLUSIVE'),
    /**
     * 稿樣圖片:該訪談底下的圖片附件 id,列印藝品印刷訂單時放進稿樣區。
     * REQ-20260809004 起可多選(原本是單選的 `artworkAttachmentId`);
     * 沒挑圖時是空陣列,不需要另一個「不指定」的表示法。
     */
    artworkAttachmentIds: z.array(z.number().int().positive()).optional().default([]),
    // 上限對齊 SalesOrder.notes 的 @db.Text:備註預設帶入整段訪談內容,200 會被砍斷
    notes: z.string().trim().max(2000).optional().nullable(),
  })
  .refine((v) => v.unitPrice != null || v.totalPrice != null, {
    message: '請輸入單價或總價其中一項',
    path: ['unitPrice'],
  });
export type CreateInterviewReceivableInput = z.infer<
  typeof CreateInterviewReceivableSchema
>;

export const CreateInterviewReceivableResponseSchema = z.object({
  orderId: z.number().int().positive(),
  orderNo: z.string(),
  total: z.string(),
});
export type CreateInterviewReceivableResponse = z.infer<
  typeof CreateInterviewReceivableResponseSchema
>;

// ──────────────────────────────────────────────────────────────────────────
// 會計「出貨管理」完整建單(REQ-20260806002)
// ──────────────────────────────────────────────────────────────────────────

/** 出貨單品項。才數 / 模數留白時走「數量 × 單價」,見 `calcLineAmount()`。 */
export const SalesOrderLineInputSchema = z.object({
  productId: z.number().int().positive().optional().nullable(),
  productCode: z.string().trim().max(20).default('MANUAL'),
  productName: z.string().trim().min(1, '品名必填').max(60),
  productSpec: z.string().trim().max(60).optional().nullable(),
  productMaterial: z.string().trim().max(60).optional().nullable(),
  /**
   * 貨品類別:設定頁「貨品類別」的 id(REQ-20260807002 加在訪談快捷建單那條路徑)。
   * 與 `productProcessing` 同型的漏洞 —— 這條 schema 沒有它時,`update()` 重建明細
   * 會把訪談建單填的類別清成 NULL(REQ-20260811003)。
   */
  productCategoryId: z.number().int().positive().optional().nullable(),
  /**
   * 加工(燙金 / 打凸…)。原本只有訪談快捷建單那條路徑會寫入,這條 schema 沒有它,
   * 導致 `update()` 整批重建明細時會把值清掉 —— 開放編輯 POSTED 單後就會踩到
   * (REQ-20260807001)。
   * 上限對齊 DB 的 `VarChar(255)` 與 `CreateInterviewReceivableSchema`:
   * 加工自 REQ-20260810001 起可複選串接,留在 60 會讓訪談建的單編輯時送不出去。
   */
  productProcessing: z.string().trim().max(255).optional().nullable(),
  unitName: z.string().trim().max(30).optional().nullable(),
  qty: positiveDecimalString,
  /**
   * 單價與總價擇一(REQ-20260905007)。規則與 `CreateInterviewReceivableSchema`
   * 的同一組欄位相同 —— 報價談的是整批總價時,逼使用者自己除以數量得到的單價
   * 會有除不盡的尾差,而那一分錢會落到應收金額與對帳上。兩者都由後端換算,
   * 前端不決定金額。`totalPrice` **不入庫**:它是輸入方式,不是單據欄位
   * (存進 DB 的仍然是 `unitPrice` 與 `amount`)。
   */
  unitPrice: nonNegativeDecimalString.optional(),
  totalPrice: nonNegativeDecimalString.optional().nullable(),
  sizeUnits: nonNegativeDecimalString.optional().nullable(),
  moldCount: nonNegativeDecimalString.optional().nullable(),
  discount: nonNegativeDecimalString.optional(),
  notes: z.string().trim().max(200).optional().nullable(),
})
  .refine((v) => v.unitPrice != null || v.totalPrice != null, {
    message: '請輸入單價或總價其中一項',
    path: ['unitPrice'],
  });
export type SalesOrderLineInput = z.infer<typeof SalesOrderLineInputSchema>;

export const CreateSalesOrderSchema = z.object({
  customerId: z.number().int().positive(),
  /** 從訪談導入時帶上,讓出貨單回溯得到來源訪談 */
  interviewId: z.number().int().positive().optional().nullable(),
  /**
   * 業務員(REQ-20260815002)。`sales_orders.salesPersonId` 從 Phase 2D 就存在,
   * 但一直沒有任何 UI 寫得進去 —— 歷史 mdb 匯入單也是 null。
   *
   * 值是 `users.id`,**刻意不在 schema.prisma 補 `@relation`**:欄位原本就是
   * 純 `Int?` 無 FK,加關聯要動 schema 與 migration,而顯示姓名只需要 service 層
   * 多查一次 User 組字典。歷史單的值是 null,畫面顯示「未指定」。
   */
  salesPersonId: z.number().int().positive().optional().nullable(),
  txnDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '日期格式須為 YYYY-MM-DD'),
  dueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, '日期格式須為 YYYY-MM-DD')
    .optional()
    .nullable(),
  shippingAddress: z.string().trim().max(255).optional().nullable(),
  /**
   * 藝品印刷訂單的四個現場欄位(REQ-20260809002、REQ-20260810003)。單頭欄位,
   * 不是逐品項 —— 一張單只有一種印刷顏色 / 出貨方式 / 面別。
   * 驗證規則與 `CreateInterviewReceivableSchema` 逐字相同:印刷顏色 / 稿件處理
   * 可複選串接 → 255;出貨方式 / 印刷面別單選 → 60。
   */
  printColor: z.string().trim().max(255).optional().nullable(),
  artworkHandling: z.string().trim().max(255).optional().nullable(),
  shippingMethod: z.string().trim().max(60).optional().nullable(),
  printSides: z.string().trim().max(60).optional().nullable(),
  /**
   * 帳款對象(REQ-20260810002)。沒帶時後端寫入 `'COMPANY'`,舊 client 行為不變。
   * 與訪談那條路徑不同的是:這裡**不會**回頭把來源訪談標成私帳 ——
   * 那個標記是建單當下的判定,事後在會計端改單不該追溯改寫訪談。
   */
  payerType: z.enum(['COMPANY', 'PERSONAL']).optional(),
  /** 帳款抬頭 / 實際收款對象姓名。公司公帳留空即沿用客戶簡稱 */
  payerName: z.string().trim().max(100).optional().nullable(),
  /**
   * 單頭的收執憑證 / 收款方式覆寫。null / 沒帶 = 沿用客戶主檔(既有行為)。
   * id 來自設定頁既有的「收款憑證」「收款方式」兩張表,不是新的值域。
   */
  paymentCertId: z.number().int().positive().optional().nullable(),
  paymentMethodId: z.number().int().positive().optional().nullable(),
  taxRate: decimalString.optional(),
  /**
   * 稅別(REQ-20260806003)。內含 = 輸入金額已含稅,由後端反推未稅與稅額。
   * 出貨管理的建單 / 編輯表單都送這一格(REQ-20260921002)——
   * 沒帶時落在 `.default('EXCLUSIVE')`,與這格存在之前的行為逐字相同。
   */
  taxMode: z.enum(['EXCLUSIVE', 'INCLUSIVE', 'EXEMPT']).default('EXCLUSIVE'),
  plateFee: nonNegativeDecimalString.optional().nullable(),
  dieFee: nonNegativeDecimalString.optional().nullable(),
  /**
   * 稿樣圖片:**`interviewId` 那筆訪談**底下的圖片附件 id,列印藝品印刷訂單時
   * 放進稿樣區。規則與 `CreateInterviewReceivableSchema` 的同名欄位逐字相同
   * (REQ-20260809004)—— 兩條路徑寫的是同一張 join table,驗證規則分岔的話
   * 同一張單從哪個入口建的就會有不一樣的稿樣。
   * 沒挑圖時是空陣列;沒關聯訪談就不能挑圖(後端擋,見 sales-orders.service.ts)。
   */
  artworkAttachmentIds: z.array(z.number().int().positive()).optional().default([]),
  notes: z.string().trim().max(2000).optional().nullable(),
  lines: z.array(SalesOrderLineInputSchema).min(1, '至少要有一個品項'),
});
export type CreateSalesOrderInput = z.infer<typeof CreateSalesOrderSchema>;

/**
 * 更新只允許在 DRAFT 或「尚未沖帳的 POSTED」狀態(REQ-20260807001),
 * 欄位與建立相同(整批覆寫品項)—— 包含稅別:REQ-20260921002 起編輯**可以改稅別**
 * (使用者找不到地方改稅額,而稅額是稅別決定的),推翻 REQ-20260807001 §2.2 當初
 * 「編輯沿用單據原本的 `taxMode`」那條決定。稅別跟其他欄位走同一關狀態守門。
 */
export const UpdateSalesOrderSchema = CreateSalesOrderSchema.omit({ customerId: true });
export type UpdateSalesOrderInput = z.infer<typeof UpdateSalesOrderSchema>;

/** 折讓登錄(過帳後才有意義) */
export const SalesOrderAllowanceSchema = z.object({
  allowanceAmount: nonNegativeDecimalString,
  reason: z.string().trim().max(200).optional().nullable(),
});
export type SalesOrderAllowanceInput = z.infer<typeof SalesOrderAllowanceSchema>;

export const VoidOrderSchema = z.object({
  reason: z.string().trim().min(1, '請填寫作廢原因').max(200),
});
export type VoidOrderInput = z.infer<typeof VoidOrderSchema>;

export interface SalesOrderListItem {
  id: number;
  uuid: string;
  orderNo: string;
  status: string;
  source: string;
  txnDate: string;
  dueDate: string | null;
  customerId: number;
  customerSimpleName: string;
  customerFullName: string;
  subtotal: string;
  tax: string;
  total: string;
  plateFee: string | null;
  dieFee: string | null;
  allowanceAmount: string | null;
  /** 已沖金額(來自 ar_settlement_lines,以 orderNo 對應) */
  settledAmount: string;
  /** 未結餘額 = total - settledAmount - allowanceAmount */
  outstanding: string;
  lineCount: number;
  /**
   * 這張單第一筆品項的品名(REQ-20260819003)。用途:進貨單「工單 / 案號」建議清單
   * 顯示品名以降低選錯單風險。與 `lineCount` 搭配可以在畫面上組「品名 等N項」。
   *
   * **選填** —— 只有 `list()` 會帶,`listOutstandingByCustomer()` 那類呼叫不需要
   * (同 `shipmentStatus` 的理由:為它多查一次 DB 是白工)。
   */
  firstProductName?: string | null;
  interviewId: number | null;
  /**
   * 業務員(REQ-20260815002)。`salesPersonId` 是 `users.id`;`salesPersonName` 由
   * service 層另查 User 組字典帶出來(欄位無 FK,見 `CreateSalesOrderSchema`)。
   * 歷史單兩者皆為 null,畫面顯示「未指定」。
   */
  salesPersonId: number | null;
  salesPersonName: string | null;
  /**
   * 出貨進度(REQ-20260815008)。與 `settledAmount` 同一個性質 —— 不是
   * `sales_orders` 的欄位,是由 `shipment_lines` 即時聚合算出來的。
   *
   * **選填**:只有出貨管理的列表 / 明細會帶。未結應收明細
   * (`listOutstandingByCustomer()`,收款彈窗吃的那支)刻意不算 ——
   * 收款畫面用不到出貨狀態,為它多打兩次 DB 是白工。
   */
  shipmentStatus?: OrderShipmentStatus;
}

export interface SalesOrderDetail extends SalesOrderListItem {
  shippingAddress: string | null;
  notes: string | null;
  taxRate: string;
  taxMode: SalesOrderTaxMode;
  postedAt: string | null;
  voidedAt: string | null;
  voidedReason: string | null;
  createdAt: string;
  /** 藝品印刷訂單抬頭用的客戶欄位(REQ-20260806003) */
  customerCode: string | null;
  customerAddress: string | null;
  customerPaymentCert: string | null;
  customerPaymentMethod: string | null;
  /** 藝品印刷訂單的現場欄位(REQ-20260809002;印刷面別 REQ-20260810003)。歷史單為 null */
  printColor: string | null;
  artworkHandling: string | null;
  shippingMethod: string | null;
  printSides: string | null;
  /**
   * 帳款對象與單頭的憑證 / 收款方式覆寫(REQ-20260810002)。
   * 出貨管理的編輯表單要帶回原值,所以取的是 **id** 而不是名稱 ——
   * 上面 `customerPaymentCert` / `customerPaymentMethod` 是客戶主檔的名稱,兩者用途不同。
   */
  payerType: string;
  payerName: string | null;
  paymentCertId: number | null;
  paymentMethodId: number | null;
  /**
   * 稿樣圖片(REQ-20260806003;REQ-20260809004 起可多張)。
   * 沒挑圖時是空陣列;已被刪除的附件不會出現在裡面。
   */
  artworks: Array<{
    uuid: string;
    originalName: string;
  }>;
  lines: Array<{
    id: number;
    lineNo: number;
    productId: number | null;
    productCode: string;
    productName: string;
    productSpec: string | null;
    productMaterial: string | null;
    /**
     * 貨品類別:**名稱**給列印頁直接顯示(REQ-20260809002),
     * **id** 給編輯表單的下拉選單帶回原值(REQ-20260811003)。兩者並存,用途不同。
     */
    productCategoryName: string | null;
    productCategoryId: number | null;
    productProcessing: string | null;
    unitName: string | null;
    qty: string;
    unitPrice: string;
    sizeUnits: string | null;
    moldCount: string | null;
    discount: string;
    amount: string;
    notes: string | null;
  }>;
}

export interface SalesOrderListResponse {
  items: SalesOrderListItem[];
  total: number;
}
